import { AuthStore } from '../core/store/authStore';
import { ApiClient } from './ApiClient';

// ─── Device Detection ────────────────────────────────────────────────────────

export interface DeviceInfo {
  browser: string;
  os: string;
  device_type: 'desktop' | 'mobile' | 'tablet';
}

function detectDeviceInfo(): DeviceInfo {
  const ua = navigator.userAgent;

  // Browser
  let browser = 'Other';
  if (/Edg\//.test(ua))         browser = 'Edge';
  else if (/OPR\//.test(ua))    browser = 'Opera';
  else if (/Chrome\//.test(ua)) browser = 'Chrome';
  else if (/Firefox\//.test(ua))browser = 'Firefox';
  else if (/Safari\//.test(ua)) browser = 'Safari';

  // OS
  let os = 'Other';
  if (/Windows/.test(ua))      os = 'Windows';
  else if (/Mac OS X/.test(ua))os = 'macOS';
  else if (/Android/.test(ua)) os = 'Android';
  else if (/iPhone|iPad/.test(ua)) os = 'iOS';
  else if (/Linux/.test(ua))   os = 'Linux';

  // Device type
  const isMobile = /Mobi|Android|iPhone/.test(ua);
  const isTablet = /iPad|Tablet/.test(ua) || (isMobile && Math.min(window.screen.width, window.screen.height) > 480);
  const device_type = isTablet ? 'tablet' : isMobile ? 'mobile' : 'desktop';

  return { browser, os, device_type };
}

// ─── Guest Session Token ─────────────────────────────────────────────────────

const GUEST_TOKEN_KEY = 'arcade_guest_token';

function getOrCreateGuestToken(): string {
  let token = localStorage.getItem(GUEST_TOKEN_KEY);
  if (!token) {
    token = crypto.randomUUID();
    localStorage.setItem(GUEST_TOKEN_KEY, token);
  }
  return token;
}

// ─── Analytics Service ───────────────────────────────────────────────────────

let _activeGameId: string | null = null;
let _sessionStart: number | null = null;

export const AnalyticsService = {
  /** Called when a game starts */
  startSession(gameId: string): void {
    _activeGameId = gameId;
    _sessionStart = Date.now();
  },

  /** Called when a game ends (back to lobby, or page unload) */
  async endSession(): Promise<void> {
    if (!_activeGameId || !_sessionStart) return;

    const duration_ms = Date.now() - _sessionStart;
    const gameId = _activeGameId;

    // Reset immediately to avoid double-submit
    _activeGameId = null;
    _sessionStart = null;

    // Minimum threshold: ignore sessions under 1 seconds (was 5s)
    if (duration_ms < 1000) return;

    const deviceInfo = detectDeviceInfo();
    const user = AuthStore.getUser();

    if (user) {
      // Registered user
      await ApiClient.trackUserSession(user.id, gameId, duration_ms, deviceInfo);
    } else {
      // Guest user
      const token = getOrCreateGuestToken();
      await ApiClient.trackGuestSession(token, gameId, duration_ms, deviceInfo);
    }
  },

  /** Returns detected device info for current visitor */
  getDeviceInfo(): DeviceInfo {
    return detectDeviceInfo();
  },
};

// Automatically try to end session if the user closes the tab
window.addEventListener('beforeunload', () => {
  if (_activeGameId && _sessionStart) {
    // Send a synchronous beacon or just attempt standard fetch
    // Note: async fetch might not complete during unload, but beacon is preferred.
    // For simplicity, we just trigger endSession() which uses fetch.
    AnalyticsService.endSession().catch(() => {});
  }
});
